import { useEffect, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { hasToken } from "@/shared/auth/tokenStorage";
import { notifyWebReady } from "@/shared/lib/webReady";
import { ROUTES } from "@/shared/config/routes";
import type { RouteAccess } from "./types";

interface AuthGuardProps {
  access: RouteAccess;
  children: ReactNode;
}

/**
 * 가드 통과 라우트의 lazy subtree 와 함께 commit 된 뒤 native 에 webReady 를 통지한다.
 * Navigate 리다이렉트 분기에는 렌더되지 않으므로 리다이렉트 commit 에서 선발화하지 않고,
 * 페이지 전환마다 리마운트돼도 notifyWebReady 의 모듈 플래그가 1회 호출을 보장한다.
 */
const WebReadySentinel = () => {
  useEffect(() => {
    notifyWebReady();
  }, []);
  return null;
};

/** 라우트 접근 정책에 따라 진입을 제어한다. 현재는 토큰 보유 여부만 검사한다(아래 TODO). */
export const AuthGuard = ({ access, children }: AuthGuardProps) => {
  const isAuthenticated = hasToken();

  const allowed = (
    <>
      {children}
      <WebReadySentinel />
    </>
  );

  switch (access) {
    case "public":
      return allowed;

    case "guestOnly":
      if (isAuthenticated) return <Navigate to={ROUTES.MAIN} replace />;
      return allowed;

    case "authenticated":
    case "certified":
    case "plannerOnly":
      // TODO: certified / plannerOnly 분기 — 본인인증/플래너 권한 도입 시 entities/user 에서 검사
      if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
      return allowed;

    default:
      return allowed;
  }
};

export default AuthGuard;
